import { useState } from 'react';
import { Box } from '@mui/material';
import { colors } from '../../../theme';
import styled from 'styled-components';
import _ from 'lodash';

import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';

// comps
import GlobalButton from '../../../components/global/button';
import GlobalInput from '../../../components/global/input';
import Grid from './grid';
import AlertPopup from '../../../components/dialogs/alertPopup';
import AddCourse from './dialogs/add-course';
import GlobalDropdown from '../../../components/global/dropdown';
import FilterOption from '../../../components/global/filterOption';

// icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/refresh-icon.svg';
import { ReactComponent as FilterIcon } from '../../../assets/icons/filter-icon.svg';
import { ReactComponent as PrintIcon } from '../../../assets/icons/print-icon.svg';
import { ReactComponent as SortIcon } from '../../../assets/icons/sort-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus-icon.svg';
import { ReactComponent as SaveIcon } from '../../../assets/icons/refresh-icon.svg';
import { setPlan } from '../../../store/reducers/term-planner';
import TermPlanApi from '../../../api/term-plan.api';
import { toast } from 'react-toastify';

interface ReportProps {
    isFolded: boolean;
}

interface DropdownProps {
    expanded: boolean;
}

const sortByItems = [
    {
        sortId: 1,
        display: 'Subject Code',
        item: 'subjectCode',
    },
    // TODO: bring this back if / when these are used
    // {
    //     sortId: 2,
    //     display: 'Course Name',
    //     item: 'courseName',
    // },
    // {
    //     sortId: 3,
    //     display: 'CRN',
    //     item: 'CRN',
    // },
    {
        sortId: 4,
        display: 'Room Number',
        item: 'room',
    },
    {
        sortId: 5,
        display: 'Capacity (Low to High)',
        item: 'capacityLow',
    },
    {
        sortId: 6,
        display: 'Capacity (High to Low)',
        item: 'capacityHigh',
    },
];

const filterItems = [
    {
        filterId: 1,
        item: 'Active Online',
    },
    {
        filterId: 2,
        item: 'Active Distance',
    },
    {
        filterId: 3,
        item: 'Cancelled',
    },
    {
        filterId: 4,
        item: 'Moved',
    },
    // {
    //     filterId: 3,
    //     display: 'New',
    //     item: 'new',
    // },
];

const Report = (props) => {
    const { onClose } = props;
    const termPlannerState = useAppSelector((state) => state.termPlanner);

    const dispatch = useAppDispatch();

    const _termPlannerApi = new TermPlanApi();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFolded, setIsFolded] = useState(false);

    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [clickAway, setClickAway] = useState(true);

    const handleFold = () => {
        setIsFolded(!isFolded);
    };

    const [expanded, setExpanded] = useState('');

    const [selected, setSelected] = useState({
        status: [],
        type: [],
    });

    const [filter, setFilter] = useState({
        subject: '',
        crn: '',
        instructor: '',
        status: '',
    });

    const [sort, setSort] = useState('subjectCode');
    const [sortDisplay, setSortDisplay] = useState('Subject Code');

    const handleExpand = (value) => {
        let newValue = value;

        if (value === expanded) {
            newValue = '';
        }

        setExpanded(newValue);
        setClickAway(false);
    };

    const handleSort = (event: any) => {
        const {
            target: { value },
        } = event;

        setSort(value.item);
        setSortDisplay(value.display);
        setExpanded('');
    };

    const expandedClickAway = (e) => {
        if (clickAway) {
            setExpanded('');
        }
        setClickAway(true);
    };

    const handleFilter = (name: any, value: any) => {
        setFilter({
            ...filter,
            [name]: value,
        });
    };

    const handleFilterDropdown = (event: any) => {
        const {
            target: { value },
        } = event;

        setFilter({
            ...filter,
            status: value,
        });
    };

    const clearAll = () => {
        setFilter({
            subject: '',
            crn: '',
            instructor: '',
            status: '',
        });

        setSort('courseCode');
        setSortDisplay('Course Code');
    };

    const handleNoteChange = (event) => {
        const value = event.target.value;

        const termPlan = _.cloneDeep(termPlannerState.termPlan);

        termPlan.note = value;

        dispatch(setPlan(termPlan));
    };

    const handlePlanSave = async () => {
        try {
            await _termPlannerApi.SavePlan(termPlannerState.termPlan);
            toast.success('Save successful');
        } catch (error) {
            toast.error('Error saving');
        }
    };

    return (
        <Box sx={{ padding: '0 15px', fontSize: '12px', height: '87vh' }}>
            <BackToList
                onClick={() => {
                    setIsAlertOpen(true);
                }}
            >
                <Box
                    sx={{
                        fontSize: '18px',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        color: colors.blue.main,
                        cursor: 'pointer',
                        mb: '10px',
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: '16px' }} /> &nbsp; Back to List
                </Box>
            </BackToList>
            <Box
                sx={{
                    background: 'white',
                    borderRadius: '5px',
                    padding: isFolded ? '10px 20px' : '20px',
                    display: 'flex',
                    mb: '10px',
                }}
            >
                {/* Types */}
                <TypeContainer isFolded={isFolded}>
                    <div>
                        <Label>Term</Label>
                        <Type>{termPlannerState.termPlan?.termCode}</Type>
                    </div>
                    <div>
                        <Label>Type</Label>
                        <Type>{termPlannerState.termPlan?.type?.toUpperCase()}</Type>
                    </div>
                    <div>
                        <Label>Weekday</Label>
                        <Type>{termPlannerState.termPlan?.weekday}</Type>
                    </div>
                    {!isFolded && <GlobalButton variant="round" text={`print`} endIcon={<PrintIcon />} />}
                </TypeContainer>

                <Box sx={{ display: isFolded ? 'none' : 'flex', flex: 4 }}>
                    {/* Available Rooms */}
                    <Box
                        sx={{
                            borderLeft: `1px solid ${colors.border}`,
                            padding: '0 20px',
                            flex: 1,
                        }}
                    >
                        Available Rooms
                        <Box sx={{ width: '100%', marginTop: '15px' }}>
                            {AavailRooms.map((o, i) => {
                                return <Room key={i}>{o.number}</Room>;
                            })}
                        </Box>
                    </Box>

                    {/* Available Labs */}
                    <Box
                        sx={{
                            borderRight: `1px solid ${colors.border}`,
                            padding: '0 15px 0 0',
                            flex: 1,
                        }}
                    >
                        Available Labs
                        <Box sx={{ width: '100%', marginTop: '15px' }}>
                            {AavailRooms.map((o, i) => {
                                return <Room key={i}>{o.number}</Room>;
                            })}
                        </Box>
                    </Box>

                    {/* Memo */}
                    <Box sx={{ flex: 1, margin: '0px 20px 0px 25px' }}>
                        <TextArea placeholder="Leave a memo" value={termPlannerState.termPlan?.note} onChange={handleNoteChange} />
                    </Box>
                </Box>

                <ArrowContainer onClick={handleFold}>{isFolded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}</ArrowContainer>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    m: '20px 0 10px 0',
                }}
            >
                {/* Search Input & Filter/Sort Buttons Container */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mr: ' 20px',

                        '& > *': {
                            mr: '5px',
                        },
                    }}
                >
                    {/* TODO: Add filter to each input */}
                    <GlobalInput placeholder="Subject Code" type="text" name="subject" value={filter.subject} onChange={handleFilter} label="Filter By" icon={<FilterIcon />} />
                    <GlobalInput placeholder="CRN" type="text" name="crn" value={filter.crn} onChange={handleFilter} sx={{ width: '100px' }} />
                    <GlobalInput placeholder="Instructor" type="text" name="instructor" value={filter.instructor} onChange={handleFilter} />
                    <GlobalDropdown placeholder="Status" data={filterItems} field={'filterId'} name={'item'} value={filter?.status} sx={{ mr: '10px' }} width="120px" onChange={handleFilterDropdown} />
                    <GlobalDropdown label="sort by" placeholder={sortDisplay} data={sortByItems} field={'sortId'} name={'display'} value={sort} icon={<SortIcon />} onChange={handleSort} />
                    <GlobalButton variant="round" text="Clear All" secondary={'true'} endIcon={<RefreshIcon />} onClick={clearAll} />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mr: ' 20px',

                        '& > *': {
                            mr: '5px',
                        },
                    }}
                >
                    <GlobalButton
                        variant="contained"
                        text="Add New"
                        startIcon={<PlusIcon />}
                        onClick={() => {
                            setIsModalOpen(true);
                        }}
                    />

                    <GlobalButton variant="contained" text="Save" startIcon={<SaveIcon />} onClick={handlePlanSave} />
                </Box>
            </Box>
            <Box sx={{ height: `calc(100vh - ${isFolded ? '250px' : '375px'})`, overflowY: 'scroll' }}>
                <Grid sort={sort} filter={filter} />
            </Box>
            {/* TODO: need a way to detect unsaved changes*/}
            <AlertPopup open={isAlertOpen} setOpen={setIsAlertOpen} leftOnClick={onClose} rightOnClick={onClose} msg="Do you want to save changes before leaving?" />
            <AddCourse open={isModalOpen} setOpen={setIsModalOpen} />
        </Box>
    );
};

export default Report;

const BackToList = styled.div`
    display: inline-block;
`;

const TypeContainer = styled.div<ReportProps>`
    margin-right: 25px;
    flex: 1;
    display: flex;
    ${(props) =>
        props.isFolded
            ? `
            & > div {
                margin-bottom: 0;
                span {
                    margin-right: 25px;
                }
            }
        `
            : `
            flex-direction: column;
            & > div {
                margin-bottom: 15px;
            }
        `}
`;

const Type = styled.span`
    color: ${colors.blue.dark};
`;

const Room = styled.div`
    display: inline-block;
    padding: 2px 5px;
    border: 1px solid #b6b6b6;
    border-radius: 5px;
    margin-right: 10px;
    margin-bottom: 10px;
    cursor: pointer;
`;

const TextArea = styled.textarea`
    width: 100%;
    height: 100%;
    padding: 15px;
    font-family: 'Roboto';
    border: 1px solid rgba(84, 84, 84, 0.2);
    border-radius: 5px;
`;

const ArrowContainer = styled.span`
    color: ${colors.blue.main};
    cursor: pointer;
`;

const Label = styled.div`
    display: inline-block;
    margin-right: 5px;
    padding: 5px 0;
    background-color: ${colors.blue.light};
    color: ${colors.blue.main};
    border-radius: 5px;
    font-size: 10px;
    font-weight: 700;
    width: 55px;
    text-align: center;
`;

const DropdownBox = styled.div<DropdownProps>`
    border: 1px solid ${colors.blue.main};
    background-color: ${colors.white};
    position: absolute;
    top: 35px;
    left: 0;
    right: 0;
    z-index: 99;
    flex-direction: column;
    min-width: 250px;

    ${(props) =>
        props.expanded
            ? `display: flex;
        `
            : `display: none;
        `}
`;


const Sorting = styled.p`
    font-size: 12px;
    color: ${colors.blue.main};
    padding: 5px 0px 4px 10px;
    background-color: ${colors.white};
    margin: 0px;
    cursor: pointer;

    &:hover {
        background-color: ${colors.blue.hover};
    }
`;

const AavailRooms = [
    // example
    {
        id: 327,
        number: '472',
        capacity: 26,
        schoolId: null,
        typeId: null,
        comments: null,
        notes: null,
        isActive: true,
    },
    {
        id: 328,
        number: '476',
        capacity: 45,
        schoolId: null,
        typeId: null,
        comments: null,
        notes: null,
        isActive: true,
    },
    {
        id: 329,
        number: '481',
        capacity: 24,
        schoolId: null,
        typeId: null,
        comments: null,
        notes: null,
        isActive: true,
    },
    {
        id: 329,
        number: '483',
        capacity: 24,
        schoolId: null,
        typeId: null,
        comments: null,
        notes: null,
        isActive: true,
    },
    {
        id: 329,
        number: '678',
        capacity: 24,
        schoolId: null,
        typeId: null,
        comments: null,
        notes: null,
        isActive: true,
    },
    {
        id: 329,
        number: '682',
        capacity: 24,
        schoolId: null,
        typeId: null,
        comments: null,
        notes: null,
        isActive: true,
    },
    {
        id: 329,
        number: '688',
        capacity: 24,
        schoolId: null,
        typeId: null,
        comments: null,
        notes: null,
        isActive: true,
    },
    {
        id: 329,
        number: '810',
        capacity: 24,
        schoolId: null,
        typeId: null,
        comments: null,
        notes: null,
        isActive: true,
    },
    {
        id: 329,
        number: '820',
        capacity: 24,
        schoolId: null,
        typeId: null,
        comments: null,
        notes: null,
        isActive: true,
    },
];

const RoomTypes = [
    //example of room type array

    'general',
    'conference Room',
    'office',
    'classroom',
    'classLab',
    'lab',
    'computerLab',
    'lecture Theatre',
];
